import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

const OLD_TESTAMENT = [
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy',
  'Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel',
  '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles', 'Ezra',
  'Nehemiah', 'Esther', 'Job', 'Psalms', 'Proverbs',
  'Ecclesiastes', 'Song of Solomon', 'Isaiah', 'Jeremiah', 'Lamentations',
  'Ezekiel', 'Daniel', 'Hosea', 'Joel', 'Amos',
  'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk',
  'Zephaniah', 'Haggai', 'Zechariah', 'Malachi'
];

const NEW_TESTAMENT = [
  'Matthew', 'Mark', 'Luke', 'John', 'Acts',
  'Romans', '1 Corinthians', '2 Corinthians', 'Galatians', 'Ephesians',
  'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians', '1 Timothy',
  '2 Timothy', 'Titus', 'Philemon', 'Hebrews', 'James',
  '1 Peter', '2 Peter', '1 John', '2 John', '3 John',
  'Jude', 'Revelation'
];

const CHAPTER_COUNTS: Record<string, number> = {
  Genesis: 50, Exodus: 40, Leviticus: 27, Numbers: 36, Deuteronomy: 34,
  Joshua: 24, Judges: 21, Ruth: 4, '1 Samuel': 31, '2 Samuel': 24,
  '1 Kings': 22, '2 Kings': 25, '1 Chronicles': 29, '2 Chronicles': 36, Ezra: 10,
  Nehemiah: 13, Esther: 10, Job: 42, Psalms: 150, Proverbs: 31,
  Ecclesiastes: 12, 'Song of Solomon': 8, Isaiah: 66, Jeremiah: 52, Lamentations: 5,
  Ezekiel: 48, Daniel: 12, Hosea: 14, Joel: 3, Amos: 9,
  Obadiah: 1, Jonah: 4, Micah: 7, Nahum: 3, Habakkuk: 3,
  Zephaniah: 3, Haggai: 2, Zechariah: 14, Malachi: 4,
  Matthew: 28, Mark: 16, Luke: 24, John: 21, Acts: 28,
  Romans: 16, '1 Corinthians': 16, '2 Corinthians': 13, Galatians: 6, Ephesians: 6,
  Philippians: 4, Colossians: 4, '1 Thessalonians': 5, '2 Thessalonians': 3, '1 Timothy': 6,
  '2 Timothy': 4, Titus: 3, Philemon: 1, Hebrews: 13, James: 5,
  '1 Peter': 5, '2 Peter': 3, '1 John': 5, '2 John': 1, '3 John': 1,
  Jude: 1, Revelation: 22
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (message: string) => void;
}

export default function BibleSummaryDialog({ open, onOpenChange, onSubmit }: Props) {
  const [book, setBook] = useState<string>('');
  const [chapter, setChapter] = useState<string>('');

  const maxChapter = useMemo(() => (book ? CHAPTER_COUNTS[book] ?? 0 : 0), [book]);
  const showChapterInput = maxChapter > 1;
  const chapterOutOfRange = (() => {
    const n = parseInt(chapter.trim(), 10);
    return chapter.trim() !== '' && (Number.isNaN(n) || n < 1 || n > maxChapter);
  })();

  const handleBookChange = (next: string) => {
    setBook(next);
    setChapter('');
  };

  const handleSubmit = () => {
    if (!book || chapterOutOfRange) return;
    const trimmedChapter = chapter.trim();
    const message = showChapterInput && trimmedChapter
      ? `Summarize ${book} ${trimmedChapter}`
      : `Summarize ${book}`;
    onSubmit(message);
    onOpenChange(false);
    setBook('');
    setChapter('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bible Summary</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="bible-summary-book">Book</Label>
            <Select value={book} onValueChange={handleBookChange}>
              <SelectTrigger id="bible-summary-book">
                <SelectValue placeholder="Select a book" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Old Testament</SelectLabel>
                  {OLD_TESTAMENT.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>New Testament</SelectLabel>
                  {NEW_TESTAMENT.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          {showChapterInput && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="bible-summary-chapter">
                Chapter (optional, 1–{maxChapter})
              </Label>
              <Input
                id="bible-summary-chapter"
                type="number"
                min={1}
                max={maxChapter}
                placeholder="All chapters"
                value={chapter}
                onChange={(e) => setChapter(e.target.value)}
                aria-invalid={chapterOutOfRange || undefined}
              />
              {chapterOutOfRange && (
                <p className="text-xs text-destructive">
                  {book} has {maxChapter} chapter{maxChapter === 1 ? '' : 's'}.
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!book || chapterOutOfRange}>Summarize</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
